// src/pages/BotPage.js
import React, { useEffect, useMemo, useState } from "react";
import {
    getBotSettings,
    updateBotSettings,
    getBotStatus,
    getBotLogs,
    runBotTick,
    killSwitch,
    refreshBotState,
    resetBotState,
    runSmartExit,
    getTargetBotSettings,
    updateTargetBotSettings,
    getTargetBotStatus,
    getTargetBotLogs,
    runTargetBotTick,
    resetTargetBotState,
} from "../api/botApi";
import { getAuthToken } from "../apiClient";
import PutBotPanel from "./PutBotPanel";


const TABS = ["Bot Control", "Targeted Bot", "PUT Bot", "Risk Level", "Smart Exit", "Live Status", "Logs", "Kill Switch"];
const API_BASE = process.env.REACT_APP_API_BASE || "";

const LIQUID_PRESET = [
    "AAPL", "MSFT", "NVDA", "AMZN", "GOOGL", "META", "TSLA", "AMD",
    "PLTR", "AVGO", "NFLX", "SPY", "QQQ", "IWM", "SOXL", "JPM",
    "XOM", "WMT", "BAC", "CRM"
];

function getUserIdFromToken() {
    try {
        const token = getAuthToken();
        if (!token) return "";
        const parts = token.split(".");
        if (parts.length < 2) return "";
        const payload = JSON.parse(atob(parts[1]));
        return (payload?.email || "").toLowerCase();
    } catch (e) {
        return "";
    }
}

function Pill({ children }) {
    return (
        <span className="px-3 py-1 rounded-full text-xs bg-gray-800 border border-gray-700">
            {children}
        </span>
    );
}

function parseWatchlistText(text) {
    return Array.from(
        new Set(
            String(text || "")
                .split(/[\s,\n\r\t,]+/)
                .map((s) => s.trim().toUpperCase())
                .filter(Boolean)
        )
    );
}

function watchlistToText(watchlist) {
    return (watchlist || []).join(", ");
}

export default function BotPage() {
    const [active, setActive] = useState("Bot Control");
    const [settings, setSettings] = useState(null);
    const [status, setStatus] = useState(null);
    const [logs, setLogs] = useState([]);
    const [topScan, setTopScan] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [watchlistText, setWatchlistText] = useState("");
    const [smartExitResult, setSmartExitResult] = useState(null);
    const [targetSettings, setTargetSettings] = useState(null);
    const [targetStatus, setTargetStatus] = useState(null);
    const [targetLogs, setTargetLogs] = useState([]);
    const [targetResult, setTargetResult] = useState(null);

    const userId = getUserIdFromToken();

    async function fetchTopScanResults(uid) {
        try {
            const token = getAuthToken();
            const res = await fetch(`${API_BASE}/api/bot/top-trades?user_id=${encodeURIComponent(uid)}&top=10`, {
                method: "GET",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.error || `Top scan failed (${res.status})`);
            setTopScan(data);
        } catch (e) {
            setTopScan({ error: e.message || String(e), top_trades: [] });
        }
    }

    async function refreshAll() {
        if (!userId) {
            setError("Missing user session. Please log in again.");
            return;
        }
        setError("");
        try {
            const s = await getBotSettings(userId);
            setSettings(s);
            setWatchlistText(watchlistToText(s?.watchlist || []));

            const st = await getBotStatus(userId);
            setStatus(st);

            const lg = await getBotLogs(userId, 200);
            setLogs(lg.logs || []);

            await fetchTopScanResults(userId);

            const ts = await getTargetBotSettings(userId);
            setTargetSettings(ts);
            const tst = await getTargetBotStatus(userId);
            setTargetStatus(tst);
            const tl = await getTargetBotLogs(userId, 100);
            setTargetLogs(tl.logs || []);
        } catch (e) {
            setError(e.message || String(e));
        }
    }

    useEffect(() => {
        refreshAll();
    }, [userId]);

    const profiles = useMemo(() => settings?.profiles || {}, [settings]);
    const supportedBrokers = useMemo(() => settings?.supported_brokers || [], [settings]);

    async function patch(p) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            const s = await updateBotSettings(userId, p);
            setSettings(s);
            if (Array.isArray(s?.watchlist)) {
                setWatchlistText(watchlistToText(s.watchlist));
            }
            const st = await getBotStatus(userId);
            setStatus(st);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function saveWatchlist(list) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            const cleaned = Array.isArray(list) ? list : parseWatchlistText(watchlistText);
            const s = await updateBotSettings(userId, { watchlist: cleaned });
            setSettings(s);
            setWatchlistText(watchlistToText(s?.watchlist || cleaned));
            const st = await getBotStatus(userId);
            setStatus(st);
            const lg = await getBotLogs(userId, 200);
            setLogs(lg.logs || []);

            await fetchTopScanResults(userId);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function runOneTick() {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            await runBotTick(userId);
            await refreshAll();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function doRefreshState() {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            await refreshBotState(userId);
            await refreshAll();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function doResetState(clearPeak = false) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            await resetBotState(userId, clearPeak);
            await refreshAll();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function doSmartExit(execute = false) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            const mode = settings?.paper_mode ? "paper" : "live";
            const result = await runSmartExit(userId, { mode, execute, debug: true });
            setSmartExitResult(result);
            const lg = await getBotLogs(userId, 200);
            setLogs(lg.logs || []);
            const st = await getBotStatus(userId);
            setStatus(st);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function patchTargetBot(p) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            const s = await updateTargetBotSettings(userId, p);
            setTargetSettings(s);
            const st = await getTargetBotStatus(userId);
            setTargetStatus(st);
            const lg = await getTargetBotLogs(userId, 100);
            setTargetLogs(lg.logs || []);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function runTargetTick(execute = true) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            const result = await runTargetBotTick(userId, { ignore_cooldown: false, execute });
            setTargetResult(result);
            const st = await getTargetBotStatus(userId);
            setTargetStatus(st);
            const lg = await getTargetBotLogs(userId, 100);
            setTargetLogs(lg.logs || []);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function resetTargetState() {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            await resetTargetBotState(userId);
            const st = await getTargetBotStatus(userId);
            setTargetStatus(st);
            const lg = await getTargetBotLogs(userId, 100);
            setTargetLogs(lg.logs || []);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function doKill(disable = true) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            await killSwitch(userId, !disable);
            await refreshAll();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    const enabled = Boolean(settings?.enabled);

    return (
        <div className="flex gap-4">
            <div className="w-56 shrink-0">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-2">
                    <div className="text-sm font-semibold mb-2">AI Bot</div>
                    <div className="flex flex-col gap-1">
                        {TABS.map((t) => (
                            <button
                                key={t}
                                onClick={() => setActive(t)}
                                className={`text-left px-3 py-2 rounded-lg text-sm ${active === t
                                    ? "bg-gray-800 border border-gray-700"
                                    : "hover:bg-gray-800/50"
                                    }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>

                    <div className="mt-4 flex flex-col gap-2">
                        <div className="flex items-center justify-between text-xs text-gray-300">
                            <span>Status</span>
                            <Pill>{enabled ? "ON" : "OFF"}</Pill>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-300">
                            <span>Broker</span>
                            <Pill>{settings?.broker || "-"}</Pill>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-300">
                            <span>Risk</span>
                            <Pill>{settings?.risk_level || "-"}</Pill>
                        </div>
                    </div>
                </div>

                {error ? <div className="mt-3 text-red-400 text-sm">{error}</div> : null}
            </div>

            <div className="flex-1">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-lg font-semibold">{active}</div>
                            <div className="text-xs text-gray-400">
                                When Bot Lock is ON, manual trading endpoints should return 409 to prevent conflicts.
                            </div>
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            <button
                                disabled={busy}
                                onClick={refreshAll}
                                className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm"
                            >
                                Refresh
                            </button>

                            <button
                                disabled={busy}
                                onClick={doRefreshState}
                                className="px-3 py-2 rounded-lg bg-blue-900/30 border border-blue-700 text-sm"
                            >
                                Refresh State
                            </button>

                            <button
                                disabled={busy}
                                onClick={() => doResetState(false)}
                                className="px-3 py-2 rounded-lg bg-yellow-900/30 border border-yellow-700 text-sm"
                            >
                                Reset State
                            </button>

                            <button
                                disabled={busy}
                                onClick={runOneTick}
                                className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm"
                            >
                                Run 1 Tick
                            </button>
                        </div>
                    </div>

                    <DebugSummary settings={settings} status={status} />

                    <TopScanPanel topScan={topScan} refreshTopScan={() => fetchTopScanResults(userId)} busy={busy} />

                    <div className="mt-4">
                        {active === "Bot Control" && (
                            <BotControl
                                settings={settings}
                                busy={busy}
                                supportedBrokers={supportedBrokers}
                                patch={patch}
                                watchlistText={watchlistText}
                                setWatchlistText={setWatchlistText}
                                saveWatchlist={saveWatchlist}
                            />
                        )}
                        {active === "Targeted Bot" && (
                            <TargetedBotPanel
                                settings={targetSettings}
                                status={targetStatus}
                                logs={targetLogs}
                                result={targetResult}
                                busy={busy}
                                patch={patchTargetBot}
                                runTick={runTargetTick}
                                resetState={resetTargetState}
                            />
                        )}
                        {active === "PUT Bot" && (
                            <PutBotPanel />
                        )}
                        {active === "Risk Level" && (
                            <RiskLevel
                                settings={settings}
                                profiles={profiles}
                                busy={busy}
                                patch={patch}
                            />
                        )}
                        {active === "Smart Exit" && (
                            <SmartExitPanel
                                settings={settings}
                                busy={busy}
                                result={smartExitResult}
                                onSimulate={() => doSmartExit(false)}
                                onExecute={() => doSmartExit(true)}
                            />
                        )}
                        {active === "Live Status" && <LiveStatus status={status} />}
                        {active === "Logs" && <Logs logs={logs} />}
                        {active === "Kill Switch" && (
                            <KillSwitch enabled={enabled} busy={busy} doKill={doKill} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-800">
            <div className="text-sm text-gray-300">{label}</div>
            <div>{children}</div>
        </div>
    );
}

function Toggle({ value, onChange, disabled }) {
    return (
        <button
            disabled={disabled}
            onClick={() => onChange(!value)}
            className={`px-3 py-2 rounded-lg border text-sm ${value ? "bg-green-900/30 border-green-700" : "bg-gray-800 border-gray-700"
                }`}
        >
            {value ? "ON" : "OFF"}
        </button>
    );
}

function DebugSummary({ settings, status }) {
    if (!settings || !status) return null;

    const debug = status.debug || {};
    const watchlistCount =
        debug.watchlist_count ??
        status?.settings?.watchlist_count ??
        (Array.isArray(settings.watchlist) ? settings.watchlist.length : 0);

    const scanSource = debug.scan_source || (settings.scan_all ? "full_market" : "watchlist");
    const lastReason = debug.last_reason || status?.state?.last_reason || "-";
    const bestSymbol = debug.best_symbol || "-";
    const bestScore =
        debug.best_score === null || debug.best_score === undefined
            ? "-"
            : Number(debug.best_score).toFixed(2);

    return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4 mb-4">
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-xs text-gray-400 mb-1">Watchlist Count</div>
                <div className="text-lg font-semibold">{watchlistCount}</div>
            </div>

            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-xs text-gray-400 mb-1">Scan Source</div>
                <div className="text-sm font-semibold break-words">{scanSource}</div>
            </div>

            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-xs text-gray-400 mb-1">Last Best Symbol</div>
                <div className="text-lg font-semibold">{bestSymbol}</div>
            </div>

            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-xs text-gray-400 mb-1">Last Best Score</div>
                <div className="text-lg font-semibold">{bestScore}</div>
            </div>

            <div className="md:col-span-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-xs text-gray-400 mb-1">Last HOLD Reason</div>
                <div className="text-sm text-gray-200">{lastReason}</div>
            </div>
        </div>
    );
}


function TopScanPanel({ topScan, refreshTopScan, busy }) {
    const rows = topScan?.top_trades || [];
    const filters = topScan?.liquidity_filters || {};
    const tradableCount = topScan?.tradable_cache_count;

    return (
        <div className="mt-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                    <div className="text-sm font-semibold">Top 10 Scan Results</div>
                    <div className="text-xs text-gray-400">
                        Ranked after price, dollar-volume, volatility, and tradable-symbol safety checks.
                    </div>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                    {tradableCount !== null && tradableCount !== undefined ? (
                        <Pill>{tradableCount} tradable cached</Pill>
                    ) : null}
                    <button
                        disabled={busy}
                        onClick={refreshTopScan}
                        className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-xs"
                    >
                        Refresh Top 10
                    </button>
                </div>
            </div>

            {topScan?.error ? (
                <div className="text-xs text-red-300 mb-2">{topScan.error}</div>
            ) : null}

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
                    <div className="text-[11px] text-gray-500">Min Price</div>
                    <div className="text-xs">${filters.min_price ?? "-"}</div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
                    <div className="text-[11px] text-gray-500">Max Price</div>
                    <div className="text-xs">${filters.max_price ?? "-"}</div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
                    <div className="text-[11px] text-gray-500">Min $ Volume</div>
                    <div className="text-xs">{formatCompact(filters.effective_min_dollar_volume)}</div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
                    <div className="text-[11px] text-gray-500">Min Vol Ratio</div>
                    <div className="text-xs">{filters.min_volume_ratio ?? "-"}</div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
                    <div className="text-[11px] text-gray-500">Max ATR%</div>
                    <div className="text-xs">{filters.max_atr_pct ?? "-"}</div>
                </div>
            </div>

            <div className="overflow-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-left text-gray-400 border-b border-gray-800">
                            <th className="py-2 pr-2">#</th>
                            <th className="py-2 pr-2">Ticker</th>
                            <th className="py-2 pr-2">Type</th>
                            <th className="py-2 pr-2">Score</th>
                            <th className="py-2 pr-2">Confidence</th>
                            <th className="py-2 pr-2">Price</th>
                            <th className="py-2 pr-2">Exp / Strike</th>
                            <th className="py-2 pr-2">Vol× / DTE</th>
                            <th className="py-2 pr-2">RSI / OI</th>
                            <th className="py-2 pr-2">Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, idx) => {
                            const sm = r.signal_model || {};
                            return (
                                <tr key={`${r.symbol}-${idx}`} className="border-b border-gray-900">
                                    <td className="py-2 pr-2 text-gray-500">{idx + 1}</td>
                                    <td className="py-2 pr-2 font-semibold text-gray-100">{r.symbol || r.ticker}</td>
                                    <td className="py-2 pr-2">{r.asset_type === "option" ? `${r.strategy || "OPT"}` : "Equity"}</td>
                                    <td className="py-2 pr-2">{Number(r.score || 0).toFixed(1)}</td>
                                    <td className="py-2 pr-2">{Number(r.confidence || 0).toFixed(1)}%</td>
                                    <td className="py-2 pr-2">${Number(r.price || r.entryPrice || 0).toFixed(2)}</td>
                                    <td className="py-2 pr-2">{r.asset_type === "option" ? `${r.expiration || "-"} / ${r.strike || "-"}` : formatCompact(r.avg_dollar_volume_20)}</td>
                                    <td className="py-2 pr-2">{r.asset_type === "option" ? (r.dte ?? sm.dte ?? "-") : (sm.volume_ratio ?? "-")}</td>
                                    <td className="py-2 pr-2">{r.asset_type === "option" ? (r.open_interest ?? sm.open_interest ?? "-") : (sm.rsi ?? "-")}</td>
                                    <td className="py-2 pr-2 text-gray-400 max-w-[360px] truncate">{r.reason}</td>
                                </tr>
                            );
                        })}
                        {!rows.length ? (
                            <tr>
                                <td className="py-3 text-gray-500" colSpan={9}>
                                    No scan results yet. Click Refresh Top 10 or Run 1 Tick during market hours.
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function formatCompact(value) {
    const n = Number(value || 0);
    if (!Number.isFinite(n) || n <= 0) return "-";
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
}

function BotControl({
    settings,
    busy,
    supportedBrokers,
    patch,
    watchlistText,
    setWatchlistText,
    saveWatchlist,
}) {
    if (!settings) return <div className="text-sm text-gray-400">Loading…</div>;

    const watchCount = parseWatchlistText(watchlistText).length;

    return (
        <div className="space-y-2">
            <Row label="Bot Enabled">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.enabled)}
                    onChange={(v) => patch({ enabled: v })}
                />
            </Row>

            <Row label="Broker">
                <select
                    disabled={busy}
                    value={settings.broker}
                    onChange={(e) => patch({ broker: e.target.value })}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                >
                    {supportedBrokers.map((b) => (
                        <option key={b} value={b}>
                            {b === "alpaca" ? "alpaca — equities" : b === "tradier" ? "tradier — options" : b === "etrade" ? "E*TRADE — equities" : b}
                        </option>
                    ))}
                </select>
            </Row>

            <div className="text-xs text-blue-200 bg-blue-950/30 border border-blue-900 rounded-lg p-3">
                Broker routing: <strong>Alpaca</strong> and <strong>E*TRADE</strong> trade equities. <strong>Tradier</strong> trades options from the options cache.
                The broker dropdown can still be used for manual testing, but the cron can now run every enabled module below.
            </div>

            <div className="mt-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div>
                        <div className="text-sm font-semibold">Automation Modules</div>
                        <div className="text-xs text-gray-400">
                            These toggles control what the Run 1 Tick cron and Auto Exit v2 cron are allowed to run.
                        </div>
                    </div>
                    <Pill>All-broker setup</Pill>
                </div>

                <Row label="Auto Buy Enabled">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.auto_buy_enabled ?? true)}
                        onChange={(v) => patch({ auto_buy_enabled: v })}
                    />
                </Row>

                <Row label="Equities Auto Buy">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.auto_buy_equities_enabled ?? true)}
                        onChange={(v) => patch({ auto_buy_equities_enabled: v })}
                    />
                </Row>

                <Row label="Options Auto Buy">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.auto_buy_options_enabled ?? true)}
                        onChange={(v) => patch({ auto_buy_options_enabled: v })}
                    />
                </Row>

                <Row label="Auto Exit v2 Enabled">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.auto_exit_enabled ?? true)}
                        onChange={(v) => patch({ auto_exit_enabled: v })}
                    />
                </Row>

                <Row label="Equities Auto Exit">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.auto_exit_equities_enabled ?? true)}
                        onChange={(v) => patch({ auto_exit_equities_enabled: v })}
                    />
                </Row>

                <Row label="Options Auto Exit">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.auto_exit_options_enabled ?? true)}
                        onChange={(v) => patch({ auto_exit_options_enabled: v })}
                    />
                </Row>
            </div>

            <div className="mt-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div>
                        <div className="text-sm font-semibold">Broker Modules</div>
                        <div className="text-xs text-gray-400">
                            Turn on the broker modules that Auto Buy and Auto Exit v2 should include.
                        </div>
                    </div>
                    <Pill>Expandable</Pill>
                </div>

                <Row label="Alpaca Equities">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.broker_alpaca_enabled ?? true)}
                        onChange={(v) => patch({ broker_alpaca_enabled: v })}
                    />
                </Row>

                <Row label="Tradier Options">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.broker_tradier_enabled ?? true)}
                        onChange={(v) => patch({ broker_tradier_enabled: v })}
                    />
                </Row>

                <Row label="E*TRADE Equities">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.broker_etrade_enabled ?? false)}
                        onChange={(v) => patch({ broker_etrade_enabled: v })}
                    />
                </Row>

                <div className="text-xs text-yellow-200 bg-yellow-950/20 border border-yellow-900 rounded-lg p-3 mt-3">
                    E*TRADE can now be enabled after you connect OAuth tokens. Alpaca remains your default equities broker; Tradier remains your options broker.
                </div>
            </div>

            <Row label="Scan All NYSE/Nasdaq">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.scan_all)}
                    onChange={(v) => patch({ scan_all: v })}
                />
            </Row>

            <Row label="Bot Lock Account (block manual trading)">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.lock_account)}
                    onChange={(v) => patch({ lock_account: v })}
                />
            </Row>

            <Row label="Paper Mode">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.paper_mode)}
                    onChange={(v) => patch({ paper_mode: v })}
                />
            </Row>

            <Row label="Allow Live Execution (extra gate)">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.allow_live)}
                    onChange={(v) => patch({ allow_live: v })}
                />
            </Row>

            <div className="mt-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <div>
                        <div className="text-sm font-semibold">Watchlist Editor</div>
                        <div className="text-xs text-gray-400">
                            Used when Scan All is OFF. Paste commas, spaces, or one ticker per line.
                        </div>
                    </div>
                    <Pill>{watchCount} tickers</Pill>
                </div>

                <textarea
                    value={watchlistText}
                    onChange={(e) => setWatchlistText(e.target.value)}
                    disabled={busy}
                    rows={6}
                    placeholder="AAPL, NVDA, MSFT, META, AMZN"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200"
                />

                <div className="mt-3 flex gap-2 flex-wrap">
                    <button
                        disabled={busy}
                        onClick={() => saveWatchlist(parseWatchlistText(watchlistText))}
                        className="px-3 py-2 rounded-lg bg-blue-900/30 border border-blue-700 text-sm"
                    >
                        Save Watchlist
                    </button>

                    <button
                        disabled={busy}
                        onClick={() => {
                            const preset = LIQUID_PRESET.join(", ");
                            setWatchlistText(preset);
                            saveWatchlist(LIQUID_PRESET);
                        }}
                        className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm"
                    >
                        Use Liquid Preset
                    </button>

                    <button
                        disabled={busy}
                        onClick={() => {
                            setWatchlistText("");
                            saveWatchlist([]);
                        }}
                        className="px-3 py-2 rounded-lg bg-red-900/30 border border-red-700 text-sm"
                    >
                        Clear Custom Watchlist
                    </button>
                </div>
            </div>

            <div className="text-xs text-gray-400 mt-3">
                Note: E*TRADE uses OAuth tokens that can expire daily, so reconnect it when status shows token expired.
            </div>
        </div>
    );
}

function RiskLevel({ settings, profiles, busy, patch }) {
    if (!settings) return <div className="text-sm text-gray-400">Loading…</div>;

    const keys = Object.keys(profiles || {});
    const selected = profiles?.[settings.risk_level];

    return (
        <div>
            <Row label="Risk Profile">
                <select
                    disabled={busy}
                    value={settings.risk_level}
                    onChange={(e) => patch({ risk_level: e.target.value })}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                >
                    {keys.map((k) => (
                        <option key={k} value={k}>
                            {profiles[k].label}
                        </option>
                    ))}
                </select>
            </Row>

            <div className="mt-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">Profile Details</div>
                {selected ? (
                    <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                        {JSON.stringify(selected, null, 2)}
                    </pre>
                ) : (
                    <div className="text-xs text-gray-500">No profile selected.</div>
                )}
            </div>
        </div>
    );
}


function SmartExitPanel({ settings, busy, result, onSimulate, onExecute }) {
    const mode = settings?.paper_mode ? "paper" : "live";
    const liveExecutionAllowed = Boolean(settings?.paper_mode || settings?.allow_live);
    const exits = result?.exits || [];
    const errors = result?.errors || [];

    return (
        <div className="space-y-4">
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-sm font-semibold">Smart Exit v2 — Profit Protection Engine</div>
                        <div className="text-xs text-gray-400 mt-1">
                            Checks whether each open trade is still healthy and whether it already made enough profit to protect the win.
                        </div>
                    </div>
                    <div className="flex gap-2 flex-wrap items-center">
                        <Pill>Mode: {mode}</Pill>
                        <Pill>Alpaca: {settings?.broker_alpaca_enabled ?? true ? "ON" : "OFF"}</Pill>
                        <Pill>Tradier: {settings?.broker_tradier_enabled ?? true ? "ON" : "OFF"}</Pill>
                        <Pill>E*TRADE: {settings?.broker_etrade_enabled ?? false ? "ON" : "OFF"}</Pill>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Auto Exit v2</div>
                        <div className="text-xs">{settings?.auto_exit_enabled ?? true ? "Enabled" : "Disabled"}</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Equity Exits</div>
                        <div className="text-xs">{settings?.auto_exit_equities_enabled ?? true ? "Enabled" : "Disabled"}</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Option Exits</div>
                        <div className="text-xs">{settings?.auto_exit_options_enabled ?? true ? "Enabled" : "Disabled"}</div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Tracks</div>
                        <div className="text-xs">Current P/L, Peak P/L, Giveback</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Protects</div>
                        <div className="text-xs">Dollar wins + green-to-red prevention</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Still Checks</div>
                        <div className="text-xs">Trend, RSI, stoch, sharp drops, DTE</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Exit Types</div>
                        <div className="text-xs">Take profit or cut loss</div>
                    </div>
                </div>

                <div className="flex gap-2 flex-wrap mt-4">
                    <button
                        disabled={busy}
                        onClick={onSimulate}
                        className="px-3 py-2 rounded-lg bg-blue-900/30 border border-blue-700 text-sm"
                    >
                        Smart Exit Simulate
                    </button>
                    <button
                        disabled={busy || !liveExecutionAllowed}
                        onClick={onExecute}
                        className="px-3 py-2 rounded-lg bg-red-900/40 border border-red-700 text-sm"
                    >
                        Smart Exit Execute
                    </button>
                </div>

                {!liveExecutionAllowed ? (
                    <div className="text-xs text-yellow-300 mt-3">
                        Live execution is blocked because Paper Mode is OFF and Allow Live Execution is OFF.
                    </div>
                ) : null}
            </div>

            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-3">Latest Smart Exit Result</div>
                {!result ? (
                    <div className="text-xs text-gray-500">No Smart Exit run yet. Click simulate first.</div>
                ) : (
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                                <div className="text-[11px] text-gray-500">Executed</div>
                                <div className="text-xs">{String(Boolean(result.execute))}</div>
                            </div>
                            <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                                <div className="text-[11px] text-gray-500">Mode</div>
                                <div className="text-xs">{result.mode || mode}</div>
                            </div>
                            <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                                <div className="text-[11px] text-gray-500">Exit Count</div>
                                <div className="text-xs">{exits.length}</div>
                            </div>
                            <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                                <div className="text-[11px] text-gray-500">Errors</div>
                                <div className="text-xs">{errors.length}</div>
                            </div>
                        </div>

                        {errors.length ? (
                            <div className="bg-red-950/30 border border-red-900 rounded-lg p-3 text-xs text-red-200">
                                {errors.map((e, i) => <div key={i}>{e}</div>)}
                            </div>
                        ) : null}

                        <div className="overflow-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-gray-400 border-b border-gray-800">
                                        <th className="py-2 pr-2">Broker</th>
                                        <th className="py-2 pr-2">Type</th>
                                        <th className="py-2 pr-2">Symbol</th>
                                        <th className="py-2 pr-2">Option</th>
                                        <th className="py-2 pr-2">Qty</th>
                                        <th className="py-2 pr-2">P/L $</th>
                                        <th className="py-2 pr-2">Peak $</th>
                                        <th className="py-2 pr-2">Giveback $</th>
                                        <th className="py-2 pr-2">Decision</th>
                                        <th className="py-2 pr-2">Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {exits.map((x, i) => (
                                        <tr key={`${x.symbol}-${i}`} className="border-b border-gray-900">
                                            <td className="py-2 pr-2">{x.broker}</td>
                                            <td className="py-2 pr-2">{x.asset_type}</td>
                                            <td className="py-2 pr-2 font-semibold">{x.symbol}</td>
                                            <td className="py-2 pr-2">{x.option_symbol || "-"}</td>
                                            <td className="py-2 pr-2">{x.qty}</td>
                                            <td className="py-2 pr-2">${Number(x.pnl_usd || 0).toFixed(2)}</td>
                                            <td className="py-2 pr-2">${Number(x.peak_profit_usd || 0).toFixed(2)}</td>
                                            <td className="py-2 pr-2">${Number(x.giveback_usd || 0).toFixed(2)}</td>
                                            <td className="py-2 pr-2">{x.decision}</td>
                                            <td className="py-2 pr-2 text-gray-400 max-w-[360px] truncate">{x.reason}</td>
                                        </tr>
                                    ))}
                                    {!exits.length ? (
                                        <tr>
                                            <td className="py-3 text-gray-500" colSpan={10}>
                                                No exits triggered. That usually means the positions are still within the hold rules.
                                            </td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function TargetedBotPanel({ settings, status, logs, result, busy, patch, runTick, resetState }) {
    const s = settings || {};
    const signal = status?.signal || status?.state?.last_signal || result?.signal || null;
    const positions = status?.positions || result?.positions || [];
    const state = status?.state || {};

    function num(v, digits = 2) {
        const n = Number(v || 0);
        if (!Number.isFinite(n)) return "-";
        return n.toFixed(digits);
    }

    return (
        <div className="space-y-4">
            <div className="bg-blue-950/20 border border-blue-900 rounded-xl p-4">
                <div className="text-sm font-semibold mb-1">Targeted Ticker Bot</div>
                <div className="text-xs text-gray-300">
                    This bot watches one ticker only. It checks sell/profit protection first, then looks for a buy setup if no matching position is open.
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-gray-950 border border-gray-800 rounded-xl p-4 space-y-2">
                    <Row label="Targeted Bot Enabled">
                        <Toggle disabled={busy} value={Boolean(s.enabled)} onChange={(v) => patch({ enabled: v })} />
                    </Row>

                    <Row label="Target Ticker">
                        <input
                            disabled={busy}
                            value={s.ticker || ""}
                            onChange={(e) => patch({ ticker: e.target.value.toUpperCase() })}
                            placeholder="TSLA"
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-32 text-right"
                        />
                    </Row>

                    <Row label="Broker / Asset Type">
                        <select
                            disabled={busy}
                            value={s.broker || "alpaca"}
                            onChange={(e) => patch({ broker: e.target.value })}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                        >
                            <option value="alpaca">Alpaca — targeted equities</option>
                            <option value="tradier">Tradier — targeted options</option>
                        </select>
                    </Row>

                    <Row label="Risk Level">
                        <select
                            disabled={busy}
                            value={s.risk_level || "balanced"}
                            onChange={(e) => patch({ risk_level: e.target.value })}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                        >
                            <option value="conservative">Conservative</option>
                            <option value="balanced">Balanced</option>
                            <option value="aggressive">Aggressive</option>
                        </select>
                    </Row>

                    <Row label="Paper Mode">
                        <Toggle disabled={busy} value={Boolean(s.paper_mode)} onChange={(v) => patch({ paper_mode: v })} />
                    </Row>

                    <Row label="Allow Live Execution">
                        <Toggle disabled={busy} value={Boolean(s.allow_live)} onChange={(v) => patch({ allow_live: v })} />
                    </Row>

                    <Row label="Auto Buy">
                        <Toggle disabled={busy} value={Boolean(s.auto_buy)} onChange={(v) => patch({ auto_buy: v })} />
                    </Row>

                    <Row label="Auto Sell / Profit Protect">
                        <Toggle disabled={busy} value={Boolean(s.auto_sell)} onChange={(v) => patch({ auto_sell: v })} />
                    </Row>
                </div>

                <div className="bg-gray-950 border border-gray-800 rounded-xl p-4 space-y-2">
                    <Row label="Max Equity Position $">
                        <input
                            disabled={busy}
                            type="number"
                            value={s.max_position_usd ?? 500}
                            onChange={(e) => patch({ max_position_usd: Number(e.target.value) })}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-32 text-right"
                        />
                    </Row>

                    <Row label="Fixed Equity Qty (0 = auto)">
                        <input
                            disabled={busy}
                            type="number"
                            value={s.fixed_qty ?? 0}
                            onChange={(e) => patch({ fixed_qty: Number(e.target.value) })}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-32 text-right"
                        />
                    </Row>

                    <Row label="Option Qty">
                        <input
                            disabled={busy}
                            type="number"
                            value={s.option_qty ?? 1}
                            onChange={(e) => patch({ option_qty: Number(e.target.value) })}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-32 text-right"
                        />
                    </Row>

                    <Row label="Buy Score Threshold (0 = profile default)">
                        <input
                            disabled={busy}
                            type="number"
                            value={s.buy_score_threshold ?? 0}
                            onChange={(e) => patch({ buy_score_threshold: Number(e.target.value) })}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-32 text-right"
                        />
                    </Row>

                    <Row label="Sell Score Threshold (0 = default)">
                        <input
                            disabled={busy}
                            type="number"
                            value={s.sell_score_threshold ?? 0}
                            onChange={(e) => patch({ sell_score_threshold: Number(e.target.value) })}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-32 text-right"
                        />
                    </Row>

                    <Row label="Stop Loss %">
                        <input
                            disabled={busy}
                            type="number"
                            value={s.stop_loss_pct ?? -8}
                            onChange={(e) => patch({ stop_loss_pct: Number(e.target.value) })}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-32 text-right"
                        />
                    </Row>

                    <Row label="Take Profit %">
                        <input
                            disabled={busy}
                            type="number"
                            value={s.take_profit_pct ?? 15}
                            onChange={(e) => patch({ take_profit_pct: Number(e.target.value) })}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-32 text-right"
                        />
                    </Row>

                    <Row label="Cooldown Seconds">
                        <input
                            disabled={busy}
                            type="number"
                            value={s.cooldown_sec ?? 300}
                            onChange={(e) => patch({ cooldown_sec: Number(e.target.value) })}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-32 text-right"
                        />
                    </Row>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                    <div className="text-xs text-gray-400">Last Price</div>
                    <div className="text-lg font-semibold">${num(signal?.price || state.last_price)}</div>
                </div>
                <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                    <div className="text-xs text-gray-400">Score</div>
                    <div className="text-lg font-semibold">{num(signal?.score || state.last_score)}</div>
                </div>
                <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                    <div className="text-xs text-gray-400">Confidence</div>
                    <div className="text-lg font-semibold">{num(signal?.confidence_pct)}%</div>
                </div>
                <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                    <div className="text-xs text-gray-400">Last Action</div>
                    <div className="text-lg font-semibold">{state.last_action || result?.action || "-"}</div>
                </div>
            </div>

            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="flex gap-2 flex-wrap mb-3">
                    <button disabled={busy} onClick={() => runTick(false)} className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm">
                        Simulate Target Tick
                    </button>
                    <button disabled={busy} onClick={() => runTick(true)} className="px-3 py-2 rounded-lg bg-blue-900/30 border border-blue-700 text-sm">
                        Run Target Tick
                    </button>
                    <button disabled={busy} onClick={resetState} className="px-3 py-2 rounded-lg bg-yellow-900/30 border border-yellow-700 text-sm">
                        Reset Target State
                    </button>
                </div>

                <div className="text-xs text-gray-400 mb-1">Last Decision Reason</div>
                <div className="text-sm text-gray-200 mb-4">{state.last_reason || result?.reason || "No targeted decision yet."}</div>

                <div className="text-sm font-semibold mb-2">Open Target Position</div>
                <div className="overflow-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-gray-400 border-b border-gray-800">
                                <th className="py-2 pr-2">Asset</th>
                                <th className="py-2 pr-2">Symbol</th>
                                <th className="py-2 pr-2">Option</th>
                                <th className="py-2 pr-2">Qty</th>
                                <th className="py-2 pr-2">P/L %</th>
                                <th className="py-2 pr-2">Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {positions.map((p, i) => (
                                <tr key={i} className="border-b border-gray-900">
                                    <td className="py-2 pr-2">{p.asset_type}</td>
                                    <td className="py-2 pr-2 font-semibold">{p.symbol}</td>
                                    <td className="py-2 pr-2">{p.option_symbol || "-"}</td>
                                    <td className="py-2 pr-2">{p.qty}</td>
                                    <td className="py-2 pr-2">{num(p.pnl_pct)}%</td>
                                    <td className="py-2 pr-2">${num(p.market_value || p.current_price)}</td>
                                </tr>
                            ))}
                            {!positions.length ? (
                                <tr><td className="py-3 text-gray-500" colSpan={6}>No open position for the targeted ticker.</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">Target Bot Logs</div>
                <div className="max-h-[320px] overflow-auto">
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-900">
                            <tr className="text-left">
                                <th className="p-2 border-b border-gray-800">Time</th>
                                <th className="p-2 border-b border-gray-800">Level</th>
                                <th className="p-2 border-b border-gray-800">Event</th>
                                <th className="p-2 border-b border-gray-800">Symbol</th>
                                <th className="p-2 border-b border-gray-800">Payload</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(logs || []).map((l) => (
                                <tr key={l.id} className="border-b border-gray-900">
                                    <td className="p-2 text-gray-300">{l.ts}</td>
                                    <td className="p-2 text-gray-300">{l.level}</td>
                                    <td className="p-2 text-gray-200">{l.event}</td>
                                    <td className="p-2 text-gray-200">{l.symbol}</td>
                                    <td className="p-2 text-gray-400"><div className="max-w-[520px] truncate">{l.payload_json}</div></td>
                                </tr>
                            ))}
                            {!logs?.length ? (
                                <tr><td className="p-3 text-gray-500" colSpan={5}>No targeted bot logs yet.</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function LiveStatus({ status }) {
    if (!status) return <div className="text-sm text-gray-400">Loading…</div>;
    const st = status.state || {};
    const s = status.settings || {};
    const d = status.debug || {};

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">Settings</div>
                <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                    {JSON.stringify(s, null, 2)}
                </pre>
            </div>
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">State</div>
                <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                    {JSON.stringify(st, null, 2)}
                </pre>
            </div>
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">Debug</div>
                <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                    {JSON.stringify(d, null, 2)}
                </pre>
            </div>
        </div>
    );
}

function Logs({ logs }) {
    return (
        <div className="space-y-2">
            <div className="text-xs text-gray-400">Newest first</div>
            <div className="bg-gray-950 border border-gray-800 rounded-xl overflow-hidden">
                <div className="max-h-[520px] overflow-auto">
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-900">
                            <tr className="text-left">
                                <th className="p-2 border-b border-gray-800">Time</th>
                                <th className="p-2 border-b border-gray-800">Level</th>
                                <th className="p-2 border-b border-gray-800">Event</th>
                                <th className="p-2 border-b border-gray-800">Symbol</th>
                                <th className="p-2 border-b border-gray-800">Payload</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(logs || []).map((l) => (
                                <tr key={l.id} className="border-b border-gray-900">
                                    <td className="p-2 text-gray-300">{l.ts}</td>
                                    <td className="p-2 text-gray-300">{l.level}</td>
                                    <td className="p-2 text-gray-200">{l.event}</td>
                                    <td className="p-2 text-gray-200">{l.symbol}</td>
                                    <td className="p-2 text-gray-400">
                                        <div className="max-w-[520px] truncate">{l.payload_json}</div>
                                    </td>
                                </tr>
                            ))}
                            {!logs?.length ? (
                                <tr>
                                    <td className="p-3 text-gray-500" colSpan={5}>
                                        No logs yet.
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function KillSwitch({ enabled, busy, doKill }) {
    return (
        <div className="space-y-3">
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">Emergency Stop</div>
                <div className="text-xs text-gray-400 mb-3">
                    Disables bot execution immediately. (Liquidation is not included in this MVP—add it later once your broker adapters are fully validated.)
                </div>
                <button
                    disabled={busy || !enabled}
                    onClick={() => doKill(true)}
                    className="px-4 py-2 rounded-lg bg-red-900/40 border border-red-700 text-sm"
                >
                    Kill Switch (Disable Bot)
                </button>
            </div>
        </div>
    );
}